import {
  getAllUnverifiedAndCompletedAgents,
  queryKeys,
} from '@vgrubs/components';
import { useInfiniteQuery } from '@tanstack/react-query';
import { useEffect, useRef } from 'react';
import { SimpleUserEntity } from 'components/src/types/user.types';

const PRELOAD_ROOT_MARGIN = '1000px';

const fetchUnverifiedAgents = async ({
  pageParam = 1,
  query,
}: {
  pageParam?: number;
  query: string;
}) => {
  const { data } = await getAllUnverifiedAndCompletedAgents({
    limit: 20,
    page: pageParam,
    query: query || undefined,
  });

  const agents: SimpleUserEntity[] = data?.data ?? [];
  const nextPage = agents.length === 20 ? pageParam + 1 : undefined;

  return {
    agents,
    nextPage,
  };
};

export const useVerificationsAgentsInfiniteScroll = (query: string) => {
  const observerRef = useRef<HTMLDivElement | null>(null);

  const {
    data,
    fetchNextPage,
    hasNextPage,
    isFetchingNextPage,
    status,
    refetch,
  } = useInfiniteQuery({
    queryKey: [...queryKeys.admin.verifications.agents, query],
    queryFn: ({ pageParam = 1 }) => fetchUnverifiedAgents({ pageParam, query }),
    getNextPageParam: (lastPage) => lastPage.nextPage,
    initialPageParam: 1,
    staleTime: 5 * 60 * 1000,
  });

  useEffect(() => {
    const current = observerRef.current;
    if (!current) return;

    const observer = new IntersectionObserver(
      (entries) => {
        const [entry] = entries;
        if (entry?.isIntersecting && hasNextPage && !isFetchingNextPage) {
          fetchNextPage();
        }
      },
      {
        threshold: 0,
        rootMargin: `0px 0px ${PRELOAD_ROOT_MARGIN} 0px`,
      }
    );

    observer.observe(current);

    return () => {
      observer.disconnect();
    };
  }, [hasNextPage, fetchNextPage, isFetchingNextPage]);

  const agents: SimpleUserEntity[] =
    data?.pages.flatMap((page) => page.agents) ?? [];

  return {
    agents,
    observerRef,
    isFetchingNextPage,
    status,
    refetch,
  };
};
